import { observer } from 'mobx-react-lite';
import { CloseText } from 'owa-locstrings/lib/strings/closetext.locstring.json';
import { SettingsText, FilterText } from './FilePickerCommandBar.locstring.json';
import loc from 'owa-localize';
import getAriaLabelForViewSwitcher from './utils/getAriaLabelForViewSwitcher';
import showFilePickerOption from './utils/showFilePickerOption';
import { CommandBar } from 'office-ui-fabric-react/lib/CommandBar';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/components/ContextualMenu';
import { FilePickerViewType, viewTypeIcon } from '../types/FilePickerViewSwitcher';
import FilePickerFilterView from './Filter/FilePickerFilterView';
import AttachmentItemsFilterBy from 'owa-service/lib/contract/AttachmentItemsFilterBy';

import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';

const styles = require('./FilePickerCommandBar.scss');
const commandBarStyles = { root: { padding: 0 } };

export interface FilePickerCommandBarProps {
    toggleSelectedFilePickerView: () => void;
    onCloseFilePickerClicked: () => void;
    selectedFilePickerView?: FilePickerViewType;
    supportedFilePickerViews: FilePickerViewType[];
    filterOptions: AttachmentItemsFilterBy[];
    selectedFilterOption: AttachmentItemsFilterBy;
    showFilterOptionsMenu: boolean;
    onFilterIconClicked: () => void;
    onFilterOptionClicked: (selectedFilter: AttachmentItemsFilterBy) => void;
    onFilterMenuDismissed: () => void;
    isProviderListView: boolean;
}

export default observer(function FilePickerCommandBar(props: FilePickerCommandBarProps) {
    const commandBarDiv = React.useRef<HTMLElement>();
    const getCommandBarItems = (
        selectedView: FilePickerViewType,
        supportedViews: FilePickerViewType[],
        toggleFilePickerView: () => void,
        filterOptions: AttachmentItemsFilterBy[],
        isProviderListView: boolean
    ): IContextualMenuItem[] => {
        const commandBarItems: IContextualMenuItem[] = [
            {
                className: styles.settingsButton,
                key: 'Settings',
                iconProps: { iconName: ControlIcons.Settings },
                icon: ControlIcons.Settings,
                ariaLabel: loc(SettingsText),
                title: loc(SettingsText),
                onClick: onSettingsClicked,
            },
            {
                key: 'CloseFilePicker',
                iconProps: { iconName: ControlIcons.Cancel },
                ariaLabel: loc(CloseText),
                title: loc(CloseText),
                onClick: props.onCloseFilePickerClicked,
            },
        ];
        if (isProviderListView) {
            // We only add the view switch icon if we have a view switcher, and at least two views
            // for it to switch between.
            if (selectedView !== null && supportedViews && supportedViews.length !== 1) {
                commandBarItems.unshift({
                    key: 'viewSwitcher',
                    ariaLabel: getAriaLabelForViewSwitcher(selectedView, supportedViews),
                    title: getAriaLabelForViewSwitcher(selectedView, supportedViews),
                    iconProps: { iconName: viewTypeIcon[selectedView]() },
                    onClick: toggleFilePickerView,
                });
            }
            // We only add the filter icon if the provider has filter options defined.
            if (filterOptions.length > 0) {
                commandBarItems.unshift({
                    key: 'Filter',
                    iconProps: { iconName: ControlIcons.Filter },
                    ariaLabel: loc(FilterText),
                    title: loc(FilterText),
                    onClick: props.onFilterIconClicked,
                });
            }
        }
        return commandBarItems;
    };
    const commandBarItems = getCommandBarItems(
        props.selectedFilePickerView,
        props.supportedFilePickerViews,
        props.toggleSelectedFilePickerView,
        props.filterOptions,
        props.isProviderListView
    );
    return (
        <div ref={c => (commandBarDiv.current = c)}>
            <CommandBar
                items={[]}
                farItems={commandBarItems}
                className={styles.commandBar}
                data-is-focusable={true}
                styles={commandBarStyles}
            />
            {props.showFilterOptionsMenu && (
                <FilePickerFilterView
                    filterOptions={props.filterOptions}
                    selectedFilterOption={props.selectedFilterOption}
                    getTargetElement={commandBarDiv.current}
                    onFilterOptionClicked={props.onFilterOptionClicked}
                    onFilterMenuDismissed={props.onFilterMenuDismissed}
                />
            )}
        </div>
    );
});

function onSettingsClicked() {
    showFilePickerOption();
}
